import * as React from 'react';
import {
    Alert,
    Button,
    MenuItem,
    Select,
    SelectChangeEvent,
    Snackbar,
    TextField,
    Card,
    CardContent, Typography, Grid
} from "@mui/material";
import {AddCircle, ArrowBack, FolderSpecial} from "@mui/icons-material";
import {useContext, useEffect, useState} from "react";
import {DatePicker} from "@mui/x-date-pickers";
import {ADMIN_VIEWS, CURRENCY, NEW_PROJECT_URL} from "../../../utils/constant";
import {validateProjectForm, ProjectFormErrors} from "../../../utils/projectValidation";
import dayjs from "dayjs";
import 'dayjs/locale/fr';
import useAxios from "../../../hooks/useAxios";
import {CommonContext} from "../../../contexts/CommonContext";
import {AdminProjectContext} from "../../../contexts/AdminProjectContext";
import "../../../styles/NewProjectForm.css";
import logger from "../../../utils/logger";

dayjs.locale('fr');

interface NewProjectFormProps {
    selectedMenu: (view: number) => void;
}

export default function NewProjectForm({selectedMenu}: NewProjectFormProps) {
    const {setNeedToRefreshData, previousMenu} = useContext(CommonContext);
    const {refreshProjects} = useContext(AdminProjectContext);
    const {postWithAxios} = useAxios();

    // Form state
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [contribution, setContribution] = useState<number | ''>('');
    const [currency, setCurrency] = useState<string>(CURRENCY[0].label);
    const [startDate, setStartDate] = useState<any>(dayjs());

    const [penaltyAmountPerDay, setPenaltyAmountPerDay] = useState<number | ''>(''); // montant fixe par jour
    const [dueDay, setDueDay] = useState<number | ''>(''); // jour limite de paiement (ex: 5 => 5 du mois)
    const [withdrawalDay, setWithdrawalDay] = useState<number | ''>(''); // jour de retrait mensuel
    const [cashFundAmount, setCashFundAmount] = useState<number | ''>(''); // montant fixe du fond de caisse

    // UI state
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [globalError, setGlobalError] = useState('');
    const [globalInfo, setGlobalInfo] = useState('');
    const [datePickerOpen, setDatePickerOpen] = useState(false);
    const [formErrors, setFormErrors] = useState<ProjectFormErrors>({
        name: '',
        description: '',
        contribution: '',
        startDate: '',
        dueDay: '',
        withdrawalDay: '',
        cashFundAmount: '',
        penaltyAmountPerDay: ''
    });

    const validateForm = () => {
        // Utiliser la validation centralisée
        const errors = validateProjectForm({
            name,
            description,
            contribution,
            startDate,
            dueDay,
            withdrawalDay,
            cashFundAmount,
            penaltyAmountPerDay
        });

        setFormErrors(errors);
        return Object.values(errors).every((v) => v === '');
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        setGlobalError('');

        try {
            const projectData: any = {
                name: name.trim(),
                startDate: startDate?.toISOString(),
                dueDay: dueDay === '' ? 0 : dueDay,
                withdrawalDay: withdrawalDay === '' ? 0 : withdrawalDay,
                currency,
                contribution: contribution === '' ? 0 : contribution,
                description: description.trim(),
                cashFundAmount: cashFundAmount === '' ? 0 : cashFundAmount,
                penaltyAmountPerDay: penaltyAmountPerDay === '' ? 0 : penaltyAmountPerDay
            };
            logger.debug('Creating project:', projectData);

            const response = await postWithAxios(NEW_PROJECT_URL, projectData);

            if (response.success) {
                setGlobalInfo(response.message || 'Projet créé avec succès');
                setNeedToRefreshData(true);

                // Rafraîchir la liste des projets
                await refreshProjects();

                // Retour à la liste après un court délai
                setTimeout(() => {
                    selectedMenu(ADMIN_VIEWS.PROJECT_LIST_VIEW);
                }, 1200);
            } else {
                setGlobalError(response.message || "Erreur lors de la création du projet");
            }
        } catch (error: any) {
            logger.error('Project creation error:', error);
            // Si axios fournit err.response.data.message => l'afficher proprement
            const msg = error?.response?.data?.message || (error instanceof Error ? error.message : "Erreur lors de la création du projet");
            setGlobalError(msg);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleBackClick = () => {
        selectedMenu(ADMIN_VIEWS.PROJECT_LIST_VIEW);
    };

    // Activation du bouton quand les champs minimaux sont présents (UX réactive)
    const contributionNum = contribution === '' ? 0 : contribution;
    const dueDayNum = dueDay === '' ? 0 : dueDay;
    const withdrawalDayNum = withdrawalDay === '' ? 0 : withdrawalDay;
    const cashFundAmountNum = cashFundAmount === '' ? 0 : cashFundAmount;
    const penaltyAmountPerDayNum = penaltyAmountPerDay === '' ? 0 : penaltyAmountPerDay;

    const isFormValid = name.trim() !== '' && description.trim() !== '' && contributionNum > 0
        && startDate && (dueDayNum >= 1 && dueDayNum <= 31) && (withdrawalDayNum >= 1 && withdrawalDayNum <= 31)
        && (cashFundAmountNum >= 0 && cashFundAmountNum <= 1000000) && penaltyAmountPerDayNum >= 0;

    return (
        <div className="form-container">
            {/* Section Header */}
            <div className="section-header">
                <div className="header-left">
                    <div className="section-icon">
                        <FolderSpecial/>
                    </div>
                    <h2 className="section-title">Nouveau Projet</h2>
                </div>
                <button className="back-button" onClick={handleBackClick}>
                    <ArrowBack/>
                    Retour
                </button>
            </div>

            {globalError && (
                <Alert severity="error" sx={{mb: 2}}>
                    {globalError}
                </Alert>
            )}

            <Snackbar
                open={globalInfo !== ''}
                autoHideDuration={6000}
                onClose={() => setGlobalInfo('')}
                message={globalInfo}
            />

            {/* Form Card */}
            <Card className="form-card">
                <div className="form-header">
                    <h3 className="form-title">Détails du projet</h3>
                    <p className="form-subtitle">Remplissez les informations pour créer un nouveau projet</p>
                </div>

                <CardContent className="form-content">
                    <div className="form-grid">
                        <Typography variant="subtitle1" fontWeight="bold">
                            Informations générales
                        </Typography>
                        <div className="full-width-field">
                            <TextField
                                className="styled-text-field form-field"
                                fullWidth
                                label="Nom du projet"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                error={!!formErrors.name}
                                helperText={formErrors.name}
                                required
                            />
                        </div>

                        <div className="full-width-field">
                            <TextField
                                className="styled-text-field form-field"
                                fullWidth
                                label="Description"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                error={!!formErrors.description}
                                helperText={formErrors.description}
                                multiline
                                rows={3}
                                required
                            />
                        </div>
                        <div className="full-width-field">
                            <DatePicker
                                className="styled-date-picker form-field"
                                label="Date de début"
                                value={startDate}
                                onChange={(newValue) => {
                                    setStartDate(newValue);
                                    setDatePickerOpen(false);
                                }}
                                open={datePickerOpen}
                                onOpen={() => setDatePickerOpen(true)}
                                onClose={() => setDatePickerOpen(false)}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        error: !!formErrors.startDate,
                                        helperText: formErrors.startDate || "La date de fin sera calculée automatiquement selon les participants",
                                        onClick: () => setDatePickerOpen(true),
                                    },
                                    field: { clearable: true },
                                }}
                                openTo="day"
                                views={['year', 'month', 'day']}
                            />
                        </div>
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" fontWeight="bold">
                                Finances
                            </Typography>
                        </Grid>
                        <div className="form-row">
                            <TextField
                                className="styled-text-field form-field"
                                label="Contribution mensuelle"
                                type="number"
                                fullWidth
                                value={contribution}
                                onChange={(e) => setContribution(e.target.value === '' ? '' : parseFloat(e.target.value))}
                                error={!!formErrors.contribution}
                                helperText={formErrors.contribution}
                                inputProps={{min: 0, step: 0.01}}
                                required
                            />

                            <Select
                                className="styled-select form-field currency-select"
                                fullWidth
                                value={currency}
                                onChange={(e: SelectChangeEvent) => setCurrency(e.target.value as string)}
                                displayEmpty
                            >
                                {CURRENCY.map((cy) => (
                                    <MenuItem key={cy.label} value={cy.label}>
                                        {cy.label}
                                    </MenuItem>))}
                            </Select>
                        </div>



                        {/* Section finances avancées / calendrier */}
                        <div className="form-row">
                            <TextField
                                className="styled-text-field form-field"
                                fullWidth
                                label="Montant pénalité par jour"
                                type="number"
                                value={penaltyAmountPerDay}
                                onChange={(e) => setPenaltyAmountPerDay(e.target.value === '' ? '' : parseFloat(e.target.value))}
                                helperText="Montant fixe à appliquer par jour de retard"
                                error={!!formErrors.penaltyAmountPerDay}
                                inputProps={{min: 0, step: 0.01}}
                            />

                            <TextField
                                className="styled-text-field form-field"
                                fullWidth
                                label={`Fond de caisse (${CURRENCY.find(c => c.label === currency)?.symbol || '€'})`}
                                type="number"
                                value={cashFundAmount}
                                onChange={(e) => setCashFundAmount(e.target.value === '' ? '' : parseFloat(e.target.value))}
                                helperText="Montant fixe réservé (ex: 500)"
                                error={!!formErrors.cashFundAmount}
                                inputProps={{min: 0, max: 1000000, step: 0.01}}
                            />
                        </div>
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" fontWeight="bold">
                                Calendrier de paiement
                            </Typography>
                        </Grid>
                        <div className="form-row">
                            <TextField
                                className="styled-text-field form-field"
                                fullWidth
                                label="Jour limite de paiement (ex: 5 = 5ème jour du mois)"
                                type="number"
                                value={dueDay}
                                onChange={(e) => setDueDay(e.target.value === '' ? '' : parseInt(e.target.value))}
                                helperText="Jour du mois pour le paiement"
                                error={!!formErrors.dueDay}
                                inputProps={{min: 1, max: 31}}
                            />

                            <TextField
                                className="styled-text-field form-field"
                                fullWidth
                                label="Jour de retrait (ex: 10 = 10ème jour)"
                                type="number"
                                value={withdrawalDay}
                                onChange={(e) => setWithdrawalDay(e.target.value === '' ? '' : parseInt(e.target.value))}
                                helperText="Jour du mois prévu pour les retraits"
                                error={!!formErrors.withdrawalDay}
                                inputProps={{min: 1, max: 31}}
                            />
                        </div>

                        <div className="form-actions">
                            <Button
                                className="cancel-button"
                                variant="outlined"
                                onClick={handleBackClick}
                                disabled={isSubmitting}
                            >
                                Annuler
                            </Button>

                            <Button
                                className="submit-button"
                                fullWidth
                                onClick={handleSubmit}
                                disabled={!isFormValid || isSubmitting}
                                startIcon={<AddCircle/>}
                            >
                                {isSubmitting ? 'Création en cours...' : 'Créer le projet'}
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
